import React, { useCallback, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Helmet } from "react-helmet"

import globalStyles from "@src/global.module.css"
import { API_ENDPOINTS } from "@constants"

import {
  SideContainer,
  MainContainer,
  PageBlockWrapper,
  PageBlockRow,
  PageBlockRowSection,
  PageBlockHeading,
  PageBlockContent,
  Spinner,
  DataListWrapper,
  getUrlSearchParams,
  VirtualizedTable,
  useQueryParams,
  FiltersBox,
  Search,
} from "@dit/core-frontend"
import {
  filtersList,
  defaultFilterParams,
  createTableConfig,
  LIMIT,
  onSearchItems,
  HISTORY_WORK_TYPE,
  PAGE_TITLE,
  EMPTY_TEXT,
} from "./RecentWorkUtils"
import { WorkHistorySideBar } from "@components/pages/WorkHistory/WorkHistorySidebar"

const RecentWorkBlock = ({
  isLoading,
  isRequestLoading,
  data,
  getData,
  updateResponseData,
  limit = LIMIT,
  type,
}) => {
  const isPageEditType = type === HISTORY_WORK_TYPE.EDIT
  const [page, setPage] = useState(1)
  const [searchParams] = useSearchParams()
  const { search } = getUrlSearchParams(searchParams)

  const navigate = useNavigate()
  const { params, onChange: onChangeParams } = useQueryParams(defaultFilterParams)

  const handleAddPage = useCallback(() => {
    if (data?.itemsList.length < data?.itemsTotal) {
      setPage((prev) => prev + 1)
    }
  }, [data])

  const fetchEditItems = useCallback(() => {
    getData(API_ENDPOINTS.works, {
      type: isPageEditType ? HISTORY_WORK_TYPE.EDIT : HISTORY_WORK_TYPE.VIEW,
      epp: limit,
      eppOffset: (page - 1) * limit,
      groupByDay: true,
      sortDirection: "desc",
      search: search || "",
      ...params,
    })
  }, [getData, isPageEditType, limit, page, search, params])

  useEffect(() => {
    setPage(1)
  }, [params, search])

  useEffect(() => {
    fetchEditItems(search)
  }, [getData, search, page, params, fetchEditItems])

  const tableConfig = createTableConfig(updateResponseData)

  return (
    <>
      <Helmet title={`${isPageEditType ? PAGE_TITLE.EDIT : PAGE_TITLE.VIEW} · МосВики`} />
      <PageBlockWrapper>
        <PageBlockRow place="middle">
          <PageBlockRowSection>
            <PageBlockHeading>
              {isPageEditType ? PAGE_TITLE.EDIT : PAGE_TITLE.VIEW}
            </PageBlockHeading>
            <Search
              onSearch={(value) => onSearchItems(value, searchParams, navigate)}
              defaultValue={search}
            />
          </PageBlockRowSection>
        </PageBlockRow>
        <PageBlockRow place="bottom">
          <FiltersBox
            filteredList={filtersList}
            filterParams={params}
            defaultParams={defaultFilterParams}
            setFilterParams={onChangeParams}
            isResetAllFilters={false}
          />
        </PageBlockRow>
        <PageBlockContent position="relative" minHeight="200px">
          {isLoading ? (
            <Spinner space="sp-16" wrapper="div" />
          ) : (
            <VirtualizedTable
              isLoading={isRequestLoading}
              over={10}
              tableConfig={tableConfig}
              dataList={data?.itemsList}
              itemsTotal={data?.itemsTotal}
              emptyText={isPageEditType ? EMPTY_TEXT.EDIT : EMPTY_TEXT.VIEW}
              infiniteScroll
              infiniteCount={2}
              onLoad={handleAddPage}
              addDateSeparator
            />
          )}
        </PageBlockContent>
      </PageBlockWrapper>
    </>
  )
}

export const RecentWorkPage = ({ type }) => {
  return (
    <>
      <SideContainer className={globalStyles.sideContainer}>
        <WorkHistorySideBar />
      </SideContainer>
      <MainContainer>
        <DataListWrapper>
          <RecentWorkBlock type={type} />
        </DataListWrapper>
      </MainContainer>
    </>
  )
}
